import { inject, Injectable } from '@angular/core';
import { OAuthErrorEvent, OAuthStorage } from 'angular-oauth2-oidc';
import { Identity } from '@mwe/models';
import { AppStorageService } from '../cache/app-storage.service';
import { EnvironmentService } from '../environment/environment.service';
import { AccountStateService } from '../account/state/account-state.service';
import { ApplicationPaths } from '@mwe/environment';
import { LoggingService } from '../logging/logging.service';
import { stringToBoolean } from '@mwe/utils';
import { ENVIRONMENT_SERVICE_TOKEN } from '@mwe/constants';
import { MweOAuthService } from './mwe-o-auth.service';

const LOGOUT_IN_PROGRESS_KEY = 'logout_in_progress';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthService = inject(MweOAuthService);
  private readonly oauthStorage = inject(OAuthStorage);
  private readonly appStorage = inject(AppStorageService);
  private readonly environmentService = inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN);
  private readonly accountStateService = inject(AccountStateService);
  private readonly loggingService = inject(LoggingService);

  constructor() {
    this.registerEventListener();
    this.cleanLogoutInProgress();
  }

  async configureOAuth(): Promise<boolean> {
    try {
      this.oauthService.configure(this.environmentService.getAuthConfig());
      this.oauthService.setupAutomaticSilentRefresh();
      return await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    } catch (error) {
      return Promise.resolve(false);
    }
  }

  login(reconfiguredPaths?: ApplicationPaths): void {
    if (reconfiguredPaths) {
      this.oauthService.configure({
        ...this.environmentService.getAuthConfig(),
        redirectUri: reconfiguredPaths.loginRedirectUri || this.environmentService.getAuthConfig().redirectUri,
        postLogoutRedirectUri: reconfiguredPaths.logoutRedirectUri || this.environmentService.getAuthConfig().postLogoutRedirectUri,
      });
    }
    this.oauthService.initLoginFlow();
  }

  logout(logoutRedirectUri?: string): void {
    if (this.isLogoutInProgress()) {
      return;
    }
    this.setLogoutInProgress();
    this.appStorage.clear();
    this.accountStateService.clearBeforeCollaboration();
    const redirectUri = `${window.location.origin}/${this.environmentService.getPortalCustomerType()}${logoutRedirectUri ?? ''}`;

    if (logoutRedirectUri) {
      this.oauthService.postLogoutRedirectUri = `${redirectUri}`;
    }

    if (this.oauthService.getIdToken()) {
      this.oauthService?.logOut();
    } else {
      try {
        this.oauthService
          .refreshToken()
          .then(response => this.oauthService.logOut())
          .catch(error => {
            location.href = `${redirectUri}`;
          });
      } catch (err) {
        console.error(err);
        sessionStorage.clear();
        location.href = `${redirectUri}`;
      }
    }
  }

  isAuthenticated(): boolean {
    // bypass auth during cypress execution if authorized access is required
    //dayjs(i.faelligkeitsdatum).isAfter(new Date())
    return !!(window as any).cypressAuthBypass || (this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken());
  }

  reloadUserProfile(): Promise<object> {
    return this.oauthService.loadUserProfile();
  }

  async register() {
    try {
      window.location.href = await this.oauthService.createRegistrationUrl({
        registrationUrl: this.environmentService.getRegistrationUri(),
      });
    } catch (e) {
      await this.loggingService.logError(e, 'createRegistrationUrl failed');
    }
  }

  getIdentity(): Identity {
    return this.oauthService.getIdentityClaims() as Identity;
  }

  private setLogoutInProgress(inProgress?: boolean) {
    this.oauthStorage.setItem(LOGOUT_IN_PROGRESS_KEY, `${inProgress ?? true}`);
  }

  private isLogoutInProgress(): boolean {
    return stringToBoolean(this.oauthStorage.getItem(LOGOUT_IN_PROGRESS_KEY));
  }

  private cleanLogoutInProgress() {
    this.oauthStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  }

  private registerEventListener() {
    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        const error = event as OAuthErrorEvent;

        switch (error.type) {
          case 'discovery_document_load_error':
            if (error.reason) {
              this.loggingService.logError(error.reason as Error);
            }
            this.appStorage.setAuthServerFailure({
              type: 'danger',
              title: 'home.authServerFailure.title',
              message: 'home.authServerFailure.message',
            });
            break;
          case 'code_error':
            location.href = '';
            break;
          case 'token_refresh_error':
            if (error.reason) {
              this.loggingService.logError(error.reason as Error, 'authService');
            }
            this.logout('');
            break;
          case 'jwks_load_error':
            break;
        }
      }
    });
  }
}
